import { Box, Container, Typography, Stack, Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 48px rgba(0, 0, 0, 0.4)'
      : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  marginBottom: theme.spacing(3),
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(16, 185, 129, 0.3)'
    : '0 8px 24px rgba(16, 185, 129, 0.25)',
  '& svg': {
    fontSize: '2.5rem',
    color: '#FFFFFF',
  },
}));

export default function ITConsulting() {
  const { t } = useLanguage();

  const services = [
    {
      icon: <AccountTreeOutlinedIcon />,
      title: t('service.consulting.service.strategy'),
      desc: t('service.consulting.service.strategy.desc'),
    },
    {
      icon: <AccountTreeOutlinedIcon />,
      title: t('service.consulting.service.architecture'),
      desc: t('service.consulting.service.architecture.desc'),
    },
    {
      icon: <SecurityOutlinedIcon />,
      title: t('service.consulting.service.security'),
      desc: t('service.consulting.service.security.desc'),
    },
    {
      icon: <TuneOutlinedIcon />,
      title: t('service.consulting.service.optimization'),
      desc: t('service.consulting.service.optimization.desc'),
    },
  ];

  const expertise = [
    {
      icon: <BusinessCenterOutlinedIcon />,
      title: t('service.consulting.expertise.enterprise'),
      years: t('service.consulting.expertise.enterprise.years'),
    },
    {
      icon: <CloudQueueOutlinedIcon />,
      title: t('service.consulting.expertise.cloud'),
      years: t('service.consulting.expertise.cloud.years'),
    },
    {
      icon: <VerifiedUserOutlinedIcon />,
      title: t('service.consulting.expertise.security'),
      years: t('service.consulting.expertise.security.years'),
    },
  ];

  return (
    <>
      <SEOHead titleKey="service.consulting.title" descriptionKey="service.consulting.hero.desc" />
      <Box component="main" role="main">
        {/* Hero Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            pt: { xs: 16, md: 20 },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '0 12px 40px rgba(16, 185, 129, 0.4)'
                      : '0 12px 40px rgba(16, 185, 129, 0.3)',
                }}
              >
                <EmojiObjectsOutlinedIcon sx={{ fontSize: '4rem', color: '#FFFFFF' }} />
              </Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('service.consulting.title')}
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: '800px', lineHeight: 1.8, fontSize: '1.25rem' }}
              >
                {t('service.consulting.subtitle')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '900px', lineHeight: 1.8, fontSize: '1.1rem', mt: 2 }}
              >
                {t('service.consulting.hero.desc')}
              </Typography>
            </Stack>
          </Container>
        </PageSection>

        {/* Services Section */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('service.consulting.services.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {services.map((service, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <GlassCard>
                      <Stack spacing={2}>
                        <IconWrapper>{service.icon}</IconWrapper>
                        <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem' }}>
                          {service.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}>
                          {service.desc}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* Expertise Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('service.consulting.expertise.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {expertise.map((item, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <GlassCard sx={{ textAlign: 'center' }}>
                      <Stack spacing={3} alignItems="center">
                        <IconWrapper>{item.icon}</IconWrapper>
                        <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.3rem' }}>
                          {item.title}
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {item.years}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* CTA Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          }}
        >
          <Container maxWidth="lg">
            <GlassCard sx={{ textAlign: 'center', py: 8 }}>
              <Stack spacing={3} alignItems="center">
                <Typography variant="h3" fontWeight={700}>
                  {t('service.cta.title')}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: '600px', fontSize: '1.1rem' }}
                >
                  {t('service.cta.subtitle')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  href="https://calendly.com/teknolobi"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '12px',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                          : '0 12px 40px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  {t('service.cta.button')}
                </Button>
              </Stack>
            </GlassCard>
          </Container>
        </PageSection>
      </Box>
    </>
  );
}